import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Connect from 'src/calendar/components/SyncCalendarPage/components/Connect';
import { BackLink } from 'src/common/components/BackButton';
import LoadingSpinner from 'src/common/components/LoadingSpinner';
import useSyncCalendar from 'src/calendar/components/SyncCalendarPage/hooks/useSyncCalendar';
import ListingSyncSelector from 'src/calendar/components/SyncCalendarPage/components/ListingSyncSelector';
import SyncedIntegrationsList from 'src/calendar/components/SyncCalendarPage/components/SyncedIntegrationsList';
import { getBoatsCall } from 'src/common/ducks/user';
import useAppDispatch from 'src/common/hooks/useAppDispatch';
import Header from '../../../common/components/Header';
import Layout from '../../../common/components/Layout';
import { PATHS, TITLE } from '../../../common/constants';
import s from './SyncCalendarPage.module.scss';

const SyncCalendarPage: FC = () => {
  const pageTitle = TITLE.CONNECT_CALENDAR;
  const dispatch = useAppDispatch();
  const [hasInit, setHasInit] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const hasCheckedAuthStatus = useRef(false);
  const {
    getAuthenticationStatus,
    getIntegrations,
    integrations,
    syncedBoats,
    unsyncedBoats,
    addIntegration,
    syncIntegration,
    removeIntegration,
  } = useSyncCalendar();

  const setup = useCallback(async () => {
    await dispatch(getBoatsCall({}));
    await getIntegrations();
    setHasInit(true);
  }, [dispatch, getIntegrations]);

  useEffect(() => {
    if (!hasInit) {
      setup();
    }
  }, [hasInit, setup]);

  useEffect(() => {
    if (!hasCheckedAuthStatus.current) {
      hasCheckedAuthStatus.current = true;
      getAuthenticationStatus().then((isValid) => {
        setIsAuthenticated(isValid);
      });
    }
  }, [getAuthenticationStatus]);

  return (
    <Layout>
      <Helmet>
        <title lang="en">{pageTitle}</title>
      </Helmet>

      <Header
        classNameModifier={classNames('bordered', 'fixed')}
        navigationVariant="owner-calendar-inbox"
        withSearchBar={false}
      />
      <div className={s.root}>
        <div className={s.content}>
          <BackLink
            to={PATHS.CALENDAR}
            variant="sync-calendar"
          >
            Back to Calendar
          </BackLink>
          <h1 className={s.heading}>Connect to Google Calendar</h1>
          <p className={s.description}>
            Connect to your Google Calendar to automatically sync your boat bookings between
            Getmyboat and Google calendars and stay organized.
          </p>
          {!hasInit ? (
            <LoadingSpinner />
          ) : (
            <>
              {!isAuthenticated && <Connect />}
              {isAuthenticated && (
                <ListingSyncSelector
                  list={unsyncedBoats}
                  addIntegration={addIntegration}
                />
              )}
              {isAuthenticated && !!(integrations.length) && (
                <SyncedIntegrationsList
                  list={syncedBoats}
                  syncIntegration={syncIntegration}
                  removeIntegration={removeIntegration}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SyncCalendarPage;
